import { debounce, getCacheStorage, setCacheStorage } from '_lib/utils/utils'
import store from "@/store";

const DEFAULT_LIMIT = 10
const ROOM_ROLE_KEYS = ['roles', 'my_roles']

// 缓存数据在磁盘中，web: localStorage, electron: 磁盘heybox文件夹
class DataCache {
  constructor() {
    // 修改缓存数据的promise，某个key存在promis说明这个数据正在修改中
    this.temp_cached_data_promise = {}
    this.cached_keys = {}
    this.cache_limit = {
      'user_card_cache': 5
    }
    this.room_role_cache = {} // 房间权限需要同步判断，因此房间中的roles和my_roles需要存在内存中
    this.last_leave_channel_cache = {} // 房间上次离开的channel_id
    this.timeout_map = {} // 限时缓存定时器
  }
  async setCache(key, data, isReset) {
    if (!(data && data.id)) {
      throw new Error('cache需要唯一id标识')
    }
    let setCacheOperate = async (resolve) => {
      let origin_cache = await this.getCache(key) || []
      if (isReset) {
        origin_cache = origin_cache.filter(({ id }) => id !== data.id)
        origin_cache.push(data)
      } else {
        let index = origin_cache.findIndex(({ id }) => id === data.id)
        if (index >= 0) {
          origin_cache.splice(index, 1, data)
        } else {
          origin_cache.push(data)
        }
      }
      if (origin_cache.length > this.getLimit(key)) {
        this.shiftCache(key, origin_cache)
      }
      this.addCachedKey(key, data.id)
      this.setLocalStorage(key, origin_cache)
      resolve && resolve(origin_cache)
      return origin_cache
    }

    if (!this.temp_cached_data_promise[key]) {
      // 如果当前key不存在temp_cached_data_promise，说明这个key没有尚未完成的set操作
      // 则创建新的promise，当更新了缓存数据后，promise完成
      this.temp_cached_data_promise[key] = new Promise((resolve) => {
        setCacheOperate(resolve)
      })
    } else {
      // 如果当前key存在temp_cached_data_promise，说明这个key存在尚未完成的set操作
      // setCacheOperate中getCache会等待temp_cached_data_promise，会在上一次set后结果的基础上再set
      let new_origin_cache = await setCacheOperate()
      // 如果temp_cached_data_promise还在，需要更新temp_cached_data_promise中的数据为此次set后的数据
      if (this.temp_cached_data_promise[key]) {
        let _promise = this.temp_cached_data_promise[key]
        this.temp_cached_data_promise[key] = _promise.then(() => {
          return new_origin_cache
        })
      }
    }
  }
  async delCache(key, id) {
    if (key && id) {
      let origin_cache = await this.getCache(key) || []
      this.setLocalStorage(key, origin_cache.filter(obj => id !== obj.id))
      this.delCachedKey(key, id)
    } else if (key) {
      this.cached_keys[key] = undefined
      this.setLocalStorage(key, null)
      this.delCachedKey(key)
    } else {
      Object.keys(this.cached_keys).forEach(key => {
        this.delCachedKey(key)
      })
      this.cached_keys = {}
    }
  }
  async getCache(key, id) {
    const keys = this.cached_keys[key]
    if (!keys) { // 如果还没缓存过，不需要获取本地的数据
      return null
    }
    if (id && !keys.includes(id)) { // 如果id存在，但是id不在缓存中，不需要获取本地的数据
      return null
    }
    let cache
    // 如果内存中存在临时缓存数据promise，说明对同一个key有set操作还未结束
    // 需要从temp_cached_data_promise中获取修改后的最新数据
    if (this.temp_cached_data_promise[key]) {
      cache = await this.temp_cached_data_promise[key] || {}
    } else {
      cache = await this.getLocalStorage(key) || {}
    }
    if (id) {
      return cache.find(cache_obj => cache_obj.id === id)?.data
    } else {
      return cache
    }
  }
  
  // set限时cache, timeout毫秒后自动清除缓存
  async setTimeoutCache(key, data, {timeout, isReset}) {
    if (!timeout) {
      throw new Error('限时缓存需要timeout超时时间')
    }
    await this.setCache('user_card_cache', data, isReset)
    this.timeout_map[`${key}-${data.id}`] = setTimeout(() => {
      this.delCache(key, data.id)
    }, timeout)
  }
  // del限时cache
  async delTimeoutCache(key, id) {
    await this.delCache(key, id)
    if (id) {
      let key_str = `${key}-${id}`
      if (this.timeout_map[key_str]) {
        clearTimeout(this.timeout_map[key_str])
        delete this.timeout_map[key_str]
      }
    } else {
      for (let timer of Object.values(this.timeout_map[key_str])) {
        clearTimeout(timer)
      }
      this.timeout_map = {}
    }
  }
  async getLocalStorage(key) {
    return await getCacheStorage(key) || []
  }
  // setLocalStorage增加debounce防止短时间内多次set，set成功后清除对应key的promise
  setLocalStorage = debounce(async function (key, value) {
    await setCacheStorage(key, value)
    this.temp_cached_data_promise[key] = null
  }, 300)
  setLimit(data) {
    this.cache_limit = {
      ...this.cache_limit,
      ...data,
    }
  }
  getLimit(key) {
  }
  shiftCache(key, cache) {
    if (key === 'room_data_cache' && store.getters.cur_channel_data?.channel_id) {
      const cur_channel_room_id = store.getters.cur_channel_data.room_id
      // 当前所在语音频道的房间缓存不能清除，会影响游戏内覆盖的用户数据更新
      let index = cache.findIndex(item => item.id !== cur_channel_room_id)
      cache.splice(index, 1)
    } else {
      cache.shift()
    }
  }
  addCachedKey(key, id) {
    if (!this.cached_keys[key]) {
      this.cached_keys[key] = [id]
    } else if (this.cached_keys[key] && !this.cached_keys[key].includes(id)) {
      this.cached_keys[key].push(id)
    }
  }
  delCachedKey(key, id) {
    if (key && id) {
      if (this.cached_keys[key]) {
        this.cached_keys[key] = this.cached_keys[key].filter(k => k !== id)
        if (this.cached_keys[key].length === 0) {
          this.cached_keys[key] = undefined
        }
      }
    } else if (key) {
      this.cached_keys[key] = undefined
    }
  }

  /** 房间缓存特殊处理  - start*/
  setRoomCache(data, isForceCache) {
    const room_id = data.id

    // 如果更新缓存的房间数据是当前所在房间，仅更新vuex中数据，不需要修改缓存
    if (data.id === store.getters.cur_room_whole_data.room_id && !isForceCache) {
      store.commit('SET_CUR_ROOM_WHOLE_DATA', data.data)
    } else {
      // 不在房间列表的话不缓存
      if (!store.getters.room_list.find(room => room.room_id == room_id)) {
        return
      }
      let role_cache = {}

      // 缓存时把my_roles和roles抽离出来，单独储存
      ROOM_ROLE_KEYS.forEach(key => {
        role_cache[key] = data.data[key]
        // data.data[key] = undefine
      })
      this.room_role_cache[room_id] = role_cache
      this.setCache('room_data_cache', data, true)
    }
  }
  async setRoomChannelCache(room_id, channels) {
    let cache_data = await this.getRoomCache(room_id)
    this.setRoomCache({
      id: room_id,
      data: {
        ...cache_data,
        channels: channels,
      }
    })
  }
  async getRoomCache(room_id) {
    room_id = room_id || store.getters.cur_room_whole_data.room_id
    if (!room_id) {
      return null
    }
    let cache_data = null
    if (room_id === store.getters.cur_room_whole_data.room_id) {
      cache_data = store.getters.cur_room_whole_data
    } else {
      cache_data = (await this.getCache('room_data_cache', room_id))
      let role_data = this.room_role_cache[room_id]
      // 拼接缓存时把抽离出来的my_roles和roles数据
      if (cache_data && role_data) {
        for (let k in role_data) {
          cache_data[k] = role_data[k]
        }
      }
    }
    return cache_data
  }
  async delRoomCache(room_id) {
    delete this.room_role_cache[room_id]
    await this.delCache('room_data_cache', room_id)
  }
  // 获取房间roles和my_roles数据
  getRoomRoleCache(room_id) {
    room_id = room_id || store.getters.cur_room_whole_data.room_id
    let data = (room_id === store.getters.cur_room_whole_data.room_id 
      ? store.getters.cur_room_whole_data
      : this.room_role_cache[room_id]) || {}
    let obj = {}
    ROOM_ROLE_KEYS.forEach(key => {
      obj[key] = data[key] || []
    })
    return obj
  }
  setRoomLastLeaveChannel(room_id, channel_id) {
    if (channel_id) {
      this.last_leave_channel_cache[room_id] = channel_id
    } else if (this.last_leave_channel_cache[room_id]) {
      delete this.last_leave_channel_cache[room_id]
    }
  }
  getRoomLastLeaveChannel(room_id) {
    return this.last_leave_channel_cache[room_id]
  }
}
let t = new DataCache()
window._test = t
export default t